import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { ReceivingLogApiService } from '../../../domain/receiving-log/api/receiving-log-api.service';
import { ReceivingItemDetail } from '../../../models/logs/ReceivingItemDetail';
import { QR_PAYLOAD_PARAM } from '../../../domain/receiving-log/rules/qr-items.rules';
import { itemCaption } from '../../../domain/receiving-log/rules/container-items.rules';

/**
 * view-logged-item.component.ts
 *
 * The authed page an item's QR code resolves to. Scanning a label with a
 * phone's native camera opens `{appBaseUrl}/view-logged-item?payload={itemGuid}`;
 * the existing route guards (MsalGuard + RoleGuard) put unauthenticated users
 * through the normal login flow first, then land them here.
 *
 * Reads the `payload` query parameter (the receiving item row's GUID), fetches
 * the aggregated detail (item + parent log + receipt-ledger rows) via
 * `GET /V1/receiving-item-details/{guid}`, and renders it read-only with
 * loading and not-found/error states. Display-only for now — Phase 2 adds
 * status/deep-link actions, so the fetched detail is kept as a single
 * `detail` property the future actions section can build on.
 */

/** The page's fetch/render state. */
type ViewState = 'loading' | 'loaded' | 'notFound' | 'error';

/** One label/value row of the item or log facts list. */
interface DetailRow {
  label: string;
  value: string;
  /** Render the value in the monospace/numeric style (POs, job numbers, dates). */
  mono?: boolean;
}

@Component({
  selector: 'app-view-logged-item',
  templateUrl: './view-logged-item.component.html',
  styleUrls: ['./view-logged-item.component.css'],
})
export class ViewLoggedItemComponent implements OnInit, OnDestroy {

  // #region Properties

  /** Current fetch/render state driving which template block shows. */
  public state: ViewState = 'loading';

  /** The fetched item + parent log (Phase 2 actions will hang off this too). */
  public detail: ReceivingItemDetail | null = null;

  /** The rows rendered in the facts list, built once the detail loads. */
  public rows: DetailRow[] = [];

  /** The human position line, e.g. "BOX 1 of 3". */
  public positionLine: string = '';

  private _itemGuid: string = '';
  private _querySub: Subscription | null = null;
  private _fetchSub: Subscription | null = null;

  // #endregion

  // #region Lifecycle

  constructor(
    private _route: ActivatedRoute,
    private _api: ReceivingLogApiService,
  ) {}

  /**
   * Reads the `payload` query parameter and triggers the item fetch. Subscribed
   * (not a snapshot) so a same-route navigation to a different GUID re-fetches.
   */
  ngOnInit(): void {
    this._querySub = this._route.queryParamMap.subscribe((params) => {
      this._itemGuid = params.get(QR_PAYLOAD_PARAM) || '';
      this.fetchItem();
    });
  }

  /** Tears down the query-param and in-flight fetch subscriptions. */
  ngOnDestroy(): void {
    this._querySub?.unsubscribe();
    this._fetchSub?.unsubscribe();
  }

  // #endregion

  // #region Methods

  /**
   * Fetches the item + parent log for the current GUID and maps the outcome to
   * the page state: no/blank GUID or a 404 → notFound, other failures → error.
   * Public so the error state's "Try again" button can re-run it.
   */
  public fetchItem(): void {
    this._fetchSub?.unsubscribe();

    if (!this._itemGuid.trim()) {
      this.state = 'notFound';
      return;
    }

    this.state = 'loading';
    this._fetchSub = this._api.getReceivingItem(this._itemGuid).subscribe({
      next: (detail) => {
        this.detail = detail;
        this.positionLine = this.buildPositionLine(detail);
        this.rows = this.buildRows(detail);
        this.state = 'loaded';
      },
      error: (err) => {
        console.error('Error fetching logged item:', err);
        // Errors arrive normalized as { message, details, originalError }.
        const status = err?.originalError?.status;
        this.state = status === 404 ? 'notFound' : 'error';
      },
    });
  }

  /**
   * Builds the "BOX 1 of 3" position line — the same caption printed on this
   * item's label.
   *
   * Numbered by `typeIndex` within its container type, NOT by `itemIndex`: that
   * is the item's global order across the log, so on a "3 boxes + 2 pallets"
   * delivery the first pallet's `itemIndex` is 4 while its sticker reads
   * "PALLET 1 of 2". `totalCount` is likewise per type.
   */
  private buildPositionLine(detail: ReceivingItemDetail): string {
    return itemCaption({
      itemType: detail.item.itemType || 'item',
      typeIndex: detail.item.typeIndex,
      typeTotal: detail.item.totalCount,
    });
  }

  /**
   * Builds the label/value rows for the facts list: item identity first, then
   * the parent log's context. Empty values are dropped so the list stays clean.
   */
  private buildRows(detail: ReceivingItemDetail): DetailRow[] {
    const item = detail.item;
    const log = detail.log;

    const rows: DetailRow[] = [
      { label: 'PO number', value: item.poNumber || log?.poNumber || '', mono: true },
      { label: 'Vendor', value: log?.vendor || '' },
      { label: 'Job number', value: log?.jobNumber || '', mono: true },
      { label: 'Job name', value: log?.jobName || '' },
      { label: 'JC Dept', value: log?.jcDept || '', mono: true },
      // The whole delivery's quantity ("3 BOX, 2 PALLET"), derived and stored by
      // the backend. The item row carries only its own type — it can't restate
      // the log's other container types, so there's no local fallback.
      { label: 'Quantity', value: log?.quantity || '', mono: true },
      { label: 'Items', value: log?.items || '' },
      { label: 'Shelf location', value: log?.location || '', mono: true },
      { label: 'Contact', value: log?.email || '' },
      { label: 'Facility', value: log?.storageFacility || log?.shipmentType || '' },
      { label: 'Date in', value: this.formatDate(log?.dateIn), mono: true },
      { label: 'Logged by', value: log?.loggedBy || '' },
      { label: 'Notes', value: log?.notes || '' },
      { label: 'Logged', value: this.formatDate(item.createdDate), mono: true },
    ];

    return rows.filter((r) => r.value.trim().length > 0);
  }

  /** Formats an ISO date string as a local MM/DD/YYYY date; '' when absent/unparseable. */
  private formatDate(iso: string | null | undefined): string {
    if (!iso) { return ''; }
    const date = new Date(iso);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US');
  }

  /** Line label for a receipt row: "Line 3" for a PO line, "Off-PO" otherwise. */
  public receiptLineLabel(receipt: { poLineSequence: number | null }): string {
    return receipt.poLineSequence != null ? `Line ${receipt.poLineSequence}` : 'Off-PO';
  }

  // #endregion
}
